import React, { useCallback, useMemo, useState } from 'react'

import {
  SurveyAnsweringForm,
  type SurveyFormBuilderElement,
  type SurveyFormSubmitResult,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import type { ModuleId } from '@factorialco/f0-react/dist/experimental'
import { useApolloClient } from '@apollo/client'
import {
  CompleteFormTaskMyMembershipDocument,
  CompleteFormTaskStatusChangeDocument,
  NewInboxTasksDocument,
  ProcurementsApprovalTimelineDocument,
  TaskActivityDocument,
} from 'generated/resources/graphql'

import app from 'modules/core/store/models/app'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { useSubscription } from 'modules/core/hooks/useSubscription'

import { FromQuestionnaireToForm } from 'modules/forms/components/newFormEditor/helpers'
import { useAnswerForm } from 'modules/forms/hooks/useAnswerForm'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import {
  applySurveySubmitAnswersToForm,
  mapFormToQuestionnaire,
} from 'modules/forms/lib/surveyAnsweringFormHelpers'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import { TaskCustomComponent, TaskDefinition } from 'modules/inbox_tasks/lib/abstraction'
import ProcurementPurchaseRequestTaskContent from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'

const PROCUREMENT_MODULE_ID: ModuleId = 'finance-spending'

const CompleteFormTaskContent: TaskCustomComponent<'ProcurementCompleteFormTask'> = ({
  taskable,
}) => {
  const purchaseRequest = taskable?.purchaseRequest
  const owner = purchaseRequest?.requesterEmployee?.employeeName ?? undefined
  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })
  const { access } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isCompletedBySubscription, setIsCompletedBySubscription] = useState(false)
  const completedByMeRef = React.useRef(false)

  useSubscription(CompleteFormTaskStatusChangeDocument, {
    onData: ({ data: subscriptionData }) => {
      const changedId =
        subscriptionData.data?.procurementCompleteFormTaskMarkAsDone?.completeFormTask
          ?.completeFormTaskId
      if (String(changedId) === String(taskable?.id)) {
        setIsCompletedBySubscription(true)
        // Always refetch — the subscription fires AFTER MarkAsDone completes,
        // so the backend data is guaranteed to be up-to-date at this point
        apolloClient.refetchQueries({
          include: [
            TaskActivityDocument,
            NewInboxTasksDocument,
            ProcurementsApprovalTimelineDocument,
          ],
        })
        if (!completedByMeRef.current) {
          setIsFormOpen(false)
          app.setFlash({
            type: 'correct',
            message: i18n.t('tasks.types.procurement_complete_form.completed_by_another'),
          })
        }
      }
    },
  })

  const formId = taskable?.completeFormFormId ?? undefined
  const materializedFormId = taskable?.formsMaterializedFormId ?? undefined

  const { data: membershipData } = useQuery(CompleteFormTaskMyMembershipDocument, {
    variables: {
      materializedFormId: materializedFormId ?? '',
      respondentId: access.id,
    },
    skip: !materializedFormId,
  })
  const membershipId = membershipData?.forms?.memberships?.[0]?.id ?? undefined

  const { form, loadingForm } = useLoadForm({
    formId,
    resourceType: 'Procurement::TaskForm',
  })

  const elements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form) return []
    return FromQuestionnaireToForm(mapFormToQuestionnaire(form).elements)
  }, [form])

  const { saveAnswers } = useAnswerForm()

  const handleSubmit = useCallback(
    async (answers: SurveySubmitAnswers): Promise<SurveyFormSubmitResult> => {
      if (isCompletedBySubscription) {
        return {
          success: false,
          errors: {
            form: i18n.t('tasks.types.procurement_complete_form.already_completed'),
          },
        }
      }

      if (!form || !membershipId) {
        return { success: false, errors: { form: i18n.t('common.error') } }
      }

      // Flag as completing before async save to prevent race with subscription
      completedByMeRef.current = true

      applySurveySubmitAnswersToForm(form, answers)

      const saved = await saveAnswers({
        form,
        membershipId,
        onSuccess: () => {
          form?.resetFormAnswers()
          setIsFormOpen(false)
          setIsCompletedBySubscription(true)
          // Refetch is handled by the subscription handler, which fires
          // after MarkAsDone completes — ensuring backend data is up-to-date
        },
      })

      if (!saved) {
        completedByMeRef.current = false
        return { success: false }
      }

      return { success: true }
    },
    [form, membershipId, saveAnswers, isCompletedBySubscription, apolloClient]
  )

  const detailsRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const el = detailsRef.current
    if (!el) return

    const handler = (e: MouseEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null
      const anchor = target?.closest('a[href="#open-form"]')
      if (anchor) {
        e.preventDefault()
        setIsFormOpen(true)
      }
    }

    el.addEventListener('click', handler)
    return () => el.removeEventListener('click', handler)
  }, [])

  if (!taskable) return null

  const isCompleted =
    isCompletedBySubscription || taskable?.taskStatus === 'done'
  const formTitle = purchaseRequest?.description ?? ''

  const detailsContent = (
    <DetailsItemsList
      ref={detailsRef}
      title={i18n.t('tasks.types.procurement_complete_form.attached_form')}
      tableView
      details={[
        {
          title: i18n.t('tasks.types.procurement_complete_form.form_name'),
          content: isCompleted
            ? { type: 'item' as const, text: formTitle }
            : {
                type: 'item' as const,
                text: formTitle,
                action: { type: 'navigate' as const, href: '#open-form' },
              },
        },
        {
          title: i18n.t('tasks.types.procurement_complete_form.status'),
          content: {
            type: 'status-tag' as const,
            text: isCompleted
              ? i18n.t('tasks.types.procurement_complete_form.completed')
              : i18n.t('tasks.types.procurement_complete_form.pending'),
            variant: isCompleted ? ('positive' as const) : ('neutral' as const),
          },
        },
      ]}
    />
  )

  return (
    <>
      <ProcurementPurchaseRequestTaskContent
        id={purchaseRequest?.purchaseRequestId ?? '0'}
        requesterContent={requesterContent}
        detailsContent={detailsContent}
      />
      {isFormOpen && (
        <SurveyAnsweringForm
          elements={elements}
          mode="all-questions"
          title={form?.title ?? ''}
          description={
            isCompletedBySubscription
              ? i18n.t('tasks.types.procurement_complete_form.already_completed')
              : (form?.description ?? undefined)
          }
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          loading={loadingForm}
          position="fullscreen"
          onSubmit={handleSubmit}
          module={{
            id: PROCUREMENT_MODULE_ID,
            label: i18n.t('sidebar.finance_spending'),
            href: '/procurement',
          }}
          labels={{
            empty: {
              title: i18n.t('f0.survey_answering_form.labels.empty.title'),
              description: i18n.t('f0.survey_answering_form.labels.empty.description'),
              emoji: '📝',
            },
          }}
        />
      )}
    </>
  )
}

export const CompleteFormTaskDefinition: TaskDefinition<'ProcurementCompleteFormTask'> = {
  taskableTypename: 'ProcurementCompleteFormTask',
  initialState: {},
  component: CompleteFormTaskContent,
  getTitle: ({ info }) =>
    i18n.t('tasks.types.procurement_complete_form.title', {
      taskName: info.purchaseRequest?.description ?? '',
    }),
  getMetadata: ({ info }) => [info.purchaseRequest?.description || ''],
}
